import React from 'react';
import './ApiConnectorConfigForm.css';
import C18Select00 from './C18Select00';
import {cl, globs} from '../../components/utils/utils';
import {
  HTTP_METHODS,
  AUTH_MODES,
  GRANT_TYPES,
  WIZARD_STEPS,
  getPreflightChecklist,
  isTestReady,
  getValidationErrors,
  getStepValidationErrors,
  testConnection,
  buildResponseTreeNodes,
  methodUsesBody,
} from '../utils/apiConnectorUtils';

class ApiConnectorConfigForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 0,
      showValidation: false,
      testing: false,
      testStatus: null,
      testResponse: null,
      testError: null,
      testPassed: false,
      expandedPaths: {},
    };
  }

  callProp = (name, ...args) => {
    const fn = this.props[name];
    if (fn) fn(...args);
  };

  update = (patch) => {
    this.props.onChange(Object.assign({}, this.props.config, patch));
  };

  updateField = (field, value) => {
    this.update({[field]: value});
  };

  fieldError = (key) => {
    if (!this.state.showValidation) return '';
    const errors = this.props.validationErrors;
    return (errors && errors[key]) || '';
  };

  resetTestState = () => {
    this.setState({
      testing: false,
      testStatus: null,
      testResponse: null,
      testError: null,
      testPassed: false,
      expandedPaths: {},
    });
  };

  handleAuthModeChange = (authMode) => {
    this.resetTestState();
    this.setState({showValidation: false});
    this.callProp('onValidationErrors', {});
    this.updateField('authMode', authMode);
  };

  handleSiteChange = (siteId) => {
    this.update({siteId, zoneId: ''});
  };

  updateParamRow = (field, index, patch) => {
    const rows = this.props.config[field].map((row, i) => (
      i === index ? Object.assign({}, row, patch) : row
    ));
    this.update({[field]: rows});
  };

  addParamRow = (field) => {
    this.update({
      [field]: this.props.config[field].concat([{key: '', value: ''}]),
    });
  };

  removeParamRow = (field, index) => {
    const rows = this.props.config[field].filter((_, i) => i !== index);
    this.update({[field]: rows.length ? rows : [{key: '', value: ''}]});
  };

  handleMethodChange = (method) => {
    this.resetTestState();
    this.updateField('method', method);
  };

  validateStep = (step) => {
    const errors = getStepValidationErrors(this.props.config, step);
    this.setState({showValidation: true});
    this.callProp('onValidationErrors', errors);
    return !Object.keys(errors).length;
  };

  goNext = () => {
    if (!this.validateStep(this.state.step)) return;
    this.callProp('onValidationErrors', {});
    this.setState({step: this.state.step + 1, showValidation: false});
  };

  goBack = () => {
    this.callProp('onValidationErrors', {});
    this.setState({step: Math.max(0, this.state.step - 1), showValidation: false});
  };

  handleTest = async () => {
    this.setState({
      showValidation: true,
      testing: true,
      testStatus: null,
      testResponse: null,
      testError: null,
    });
    const errors = getValidationErrors(this.props.config, 'test');
    if (Object.keys(errors).length) {
      this.callProp('onValidationErrors', errors);
      this.setState({testing: false});
      return;
    }
    this.callProp('onValidationErrors', {});
    const result = await testConnection(this.props.config);
    cl('[API Connector] handleTest result', result);
    if (result.ok) {
      this.setState({
        testing: false,
        testStatus: result.status,
        testResponse: result.data,
        testError: null,
        testPassed: true,
        expandedPaths: {},
        step: 3,
        showValidation: false,
      });
      this.callProp('onTestSuccess', result);
    } else {
      this.setState({
        testing: false,
        testStatus: result.status || 0,
        testResponse: result.data != null ? result.data : null,
        testError: result.error || 'Request failed.',
        testPassed: false,
      });
    }
  };

  handleSave = () => {
    this.setState({showValidation: true});
    const errors = getValidationErrors(this.props.config, 'save');
    if (Object.keys(errors).length) {
      this.callProp('onValidationErrors', errors);
      return;
    }
    this.callProp('onValidationErrors', {});
    this.callProp('onSave', this.props.config);
  };

  toggleTreeNode = (path) => {
    this.setState((prev) => ({
      expandedPaths: Object.assign({}, prev.expandedPaths, {
        [path]: !prev.expandedPaths[path],
      }),
    }));
  };

  selectTreePath = (path) => {
    this.updateField('responseRootPath', path);
  };

  handleTreeNodeClick = (node) => {
    if (node.isLeaf) {
      this.selectTreePath(node.path);
      return;
    }
    this.toggleTreeNode(node.path);
  };

  renderField = (id, label, value, onChange, opts = {}) => (
    <div className={`api-connector-field${opts.full ? ' full-width' : ''}${opts.error ? ' has-error' : ''}`}>
      <label htmlFor={id}>
        {label}{opts.required && <span className="required">*</span>}
      </label>
      <input
        id={id}
        type={opts.type || 'text'}
        value={value}
        placeholder={opts.placeholder || ''}
        onChange={(e) => onChange(e.currentTarget.value)}
        autoComplete={opts.type === 'password' ? 'new-password' : undefined}
      />
      {opts.hint && <span className="field-hint">{opts.hint}</span>}
      {opts.error && <span className="field-error">{opts.error}</span>}
    </div>
  );

  renderSelect = (id, label, value, options, onChange, opts = {}) => (
    <div className={`api-connector-field${opts.full ? ' full-width' : ''}`}>
      <label htmlFor={id}>{label}{opts.required && <span className="required">*</span>}</label>
      <span className="custom-select">
        <select id={id} value={value} onChange={(e) => onChange(e.currentTarget.value)}>
          {options.map((opt) => (
            <option key={opt.v || opt} value={opt.v || opt}>{opt.t || opt}</option>
          ))}
        </select>
        <span className="material-icons down-arrow">keyboard_arrow_down</span>
      </span>
      {opts.error && <span className="field-error">{opts.error}</span>}
    </div>
  );

  renderSectionHeader = (icon, iconClass, title, subtitle) => (
    <div className="api-connector-section-header">
      {icon && <span className={`material-icons-outlined section-icon ${iconClass}`}>{icon}</span>}
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </div>
  );

  renderStepNav = () => {
    const {step} = this.state;
    return (
      <div className="api-connector-steps">
        {WIZARD_STEPS.map((s) => (
          <div
            key={s.id}
            className={`api-connector-step${step === s.id ? ' active' : ''}${step > s.id ? ' done' : ''}`}
          >
            <span className="step-number">{s.id + 1}</span>
            <div className="step-copy">
              <span className="step-title">{s.title}</span>
              <span className="step-subtitle">{s.subtitle}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  renderAuthTabs = () => {
    const active = this.props.config.authMode;
    return (
      <div className="api-connector-auth-tabs" role="tablist">
        {AUTH_MODES.map((tab) => (
          <button
            key={tab.v}
            type="button"
            role="tab"
            aria-selected={active === tab.v}
            className={`auth-tab${active === tab.v ? ' active' : ''}`}
            onClick={() => this.handleAuthModeChange(tab.v)}
          >
            {tab.t}
          </button>
        ))}
      </div>
    );
  };

  renderParamSection = (field, title, hint) => {
    const rows = this.props.config[field] || [{key: '', value: ''}];
    return (
      <div className="api-connector-params-section">
        <div className="params-section-heading">
          <span className="params-section-title">{title}</span>
          {hint && <span className="params-section-hint">{hint}</span>}
        </div>
        <div className="api-connector-params-table">
          <div className="params-table-header">
            <span>Parameter</span>
            <span>Value</span>
            <span></span>
          </div>
          {rows.map((row, i) => (
            <div key={i} className="params-table-row">
              <input
                type="text"
                placeholder="param_name"
                value={row.key}
                onChange={(e) => this.updateParamRow(field, i, {key: e.currentTarget.value})}
              />
              <input
                type="text"
                placeholder="value"
                value={row.value}
                onChange={(e) => this.updateParamRow(field, i, {value: e.currentTarget.value})}
              />
              <button
                type="button"
                className="mapping-remove-btn"
                aria-label="Remove parameter"
                onClick={() => this.removeParamRow(field, i)}
              >
                <span className="material-icons-outlined">close</span>
              </button>
              {this.fieldError(`${field}.${i}`) && (
                <span className="field-error params-row-error">{this.fieldError(`${field}.${i}`)}</span>
              )}
            </div>
          ))}
        </div>
        <button type="button" className="add-row-btn" onClick={() => this.addParamRow(field)}>
          <span className="material-icons-outlined">add</span>
          Add parameter
        </button>
      </div>
    );
  };

  renderConnectionStep = () => {
    const c = this.props.config;
    return (
      <section className="api-connector-card">
        {this.renderSectionHeader('link', 'icon-link', 'Connection', 'Set the API endpoint and authentication details.')}
        <div className="api-connector-grid">
          {this.renderField('connector-base-url', 'Base URL', c.baseUrl,
            (v) => this.updateField('baseUrl', v),
            {
              full: true,
              required: true,
              placeholder: 'https://api.example.com/v2/stations',
              error: this.fieldError('baseUrl'),
            })}
          {this.renderSelect('connector-method', 'HTTP Method', c.method,
            HTTP_METHODS.map((m) => ({v: m, t: m})),
            (v) => this.handleMethodChange(v))}
          {this.renderField('connector-timeout', 'Timeout (ms)', c.timeout,
            (v) => this.updateField('timeout', v),
            {type: 'number', required: true, error: this.fieldError('timeout')})}
        </div>

        {this.renderParamSection(
          'queryParams',
          'URL query parameters',
          methodUsesBody(c.method)
            ? 'Appended to the request URL. Used with all HTTP methods.'
            : 'Appended to the request URL as ?key=value pairs.',
        )}
        {methodUsesBody(c.method) && this.renderParamSection(
          'bodyParams',
          'Request body parameters',
          'Sent as JSON in the request body for POST, PUT, and PATCH.',
        )}

        <div className="api-connector-step-divider" />
        {this.renderAuthTabs()}
        {c.authMode === 'apiKeySecret' && (
          <div className="api-connector-grid">
            {this.renderField('connector-api-key', 'API key', c.apiKey,
              (v) => this.updateField('apiKey', v),
              {type: 'password', required: true, error: this.fieldError('apiKey')})}
            {this.renderField('connector-api-secret', 'API secret', c.apiSecret,
              (v) => this.updateField('apiSecret', v),
              {type: 'password', required: true, error: this.fieldError('apiSecret')})}
          </div>
        )}
        {c.authMode === 'oauth2' && (
          <div className="api-connector-grid">
            {this.renderField('connector-token-url', 'Token URL', c.tokenUrl,
              (v) => this.updateField('tokenUrl', v),
              {
                full: true,
                required: true,
                placeholder: 'https://api.example.com/oauth/token',
                error: this.fieldError('tokenUrl'),
              })}
            {this.renderField('connector-client-id', 'Client ID', c.clientId,
              (v) => this.updateField('clientId', v),
              {required: true, error: this.fieldError('clientId')})}
            {this.renderField('connector-client-secret', 'Client secret', c.clientSecret,
              (v) => this.updateField('clientSecret', v),
              {type: 'password', required: true, error: this.fieldError('clientSecret')})}
            {this.renderField('connector-scopes', 'Scopes', c.scopes,
              (v) => this.updateField('scopes', v),
              {placeholder: 'read write'})}
            {this.renderSelect('connector-grant-type', 'Grant type', c.grantType,
              GRANT_TYPES, (v) => this.updateField('grantType', v))}
          </div>
        )}
      </section>
    );
  };

  showSitesOpts = () => (
    (globs.sitesInfo && globs.sitesInfo.info ? globs.sitesInfo.info : []).map((s, i) => (
      <option key={i} value={s.siteId}>{s.name}</option>
    ))
  );

  showZonesOpts = () => {
    const c = this.props.config;
    return (globs.zonesInfo && globs.zonesInfo.info ? globs.zonesInfo.info : [])
      .filter((z) => z.siteId === c.siteId && !z.deleted)
      .map((z, i) => (
        <option key={i} value={z.zoneId}>{z.zoneName}</option>
      ));
  };

  renderSiteZoneStep = () => {
    const c = this.props.config;
    return (
      <section className="api-connector-card">
        {this.renderSectionHeader('place', 'icon-place', 'Site & zone', 'Choose where this connector should publish data.')}
        <div className="api-connector-site-zone">
          <div className="custom-select">
            <label htmlFor="connector-site">Select Site</label>
            <C18Select00
              id="connector-site"
              value={c.siteId}
              onChange={(e) => this.handleSiteChange(e.currentTarget.value)}
            >
              <option value="">Select a site</option>
              {this.showSitesOpts()}
            </C18Select00>
            <span className="material-icons down-arrow">keyboard_arrow_down</span>
          </div>
          {this.fieldError('siteId') && <span className="field-error">{this.fieldError('siteId')}</span>}

          <div className="custom-select">
            <label htmlFor="connector-zone">Select Zone</label>
            <C18Select00
              id="connector-zone"
              value={c.zoneId}
              onChange={(e) => this.updateField('zoneId', e.currentTarget.value)}
            >
              <option value="">Select a zone</option>
              {this.showZonesOpts()}
            </C18Select00>
            <span className="material-icons down-arrow">keyboard_arrow_down</span>
          </div>
          {this.fieldError('zoneId') && <span className="field-error">{this.fieldError('zoneId')}</span>}
        </div>

        <div className="api-connector-grid">
          {this.renderField('connector-name', 'Connector name', c.name,
            (v) => this.updateField('name', v),
            {
              full: true,
              required: true,
              placeholder: 'WeatherLink Stations',
              error: this.fieldError('name'),
            })}
          {this.renderField('connector-poll-interval', 'Poll interval (seconds)', c.pollInterval,
            (v) => this.updateField('pollInterval', v),
            {
              type: 'number',
              required: true,
              hint: 'How often the backend re-fetches data from the API.',
              error: this.fieldError('pollInterval'),
            })}
        </div>
      </section>
    );
  };

  renderPreflightBadges = () => {
    const checklist = getPreflightChecklist(this.props.config);
    const c = this.props.config;
    const badge = (label, ok) => (
      <span key={label} className={`preflight-badge${ok ? ' ok' : ' pending'}`}>
        <span className="material-icons-outlined">{ok ? 'check' : 'close'}</span>
        {label}
      </span>
    );
    return (
      <div className="preflight-badges">
        {badge('Base URL configured', checklist.baseUrl)}
        {badge('Credentials provided', checklist.credentials)}
        {badge('Site selected', !!c.siteId)}
        {badge('Zone selected', !!c.zoneId)}
      </div>
    );
  };

  renderTestStep = () => {
    const c = this.props.config;
    const {testStatus, testResponse, testError, testing} = this.state;
    const hasResult = testError != null || testStatus != null;
    const ok = testStatus >= 200 && testStatus < 300;

    return (
      <section className="api-connector-card">
        {this.renderSectionHeader('wifi', 'icon-shield', 'Test connection', 'Run a live test against the third-party API.')}
        <p className="api-connector-step-help">
          Review your settings, then test the connection. A successful test will take you to the response path step.
        </p>
        <div className="api-connector-test-panel standalone">
          <div className="test-button-row">
            <button
              type="button"
              className="test-btn"
              disabled={!isTestReady(c) || testing}
              onClick={this.handleTest}
            >
              <span className="material-icons-outlined">{testing ? 'hourglass_top' : 'wifi'}</span>
              {testing ? 'Testing connection…' : 'Test connection'}
            </button>
            {this.renderPreflightBadges()}
          </div>

          {hasResult && (
            <div className={`test-result${ok ? ' success' : ' error'}`}>
              <div className="test-result-header">
                {testStatus ? (
                  <span className={`status-badge${ok ? ' ok' : ' fail'}`}>HTTP {testStatus}</span>
                ) : (
                  <span className="status-badge fail">Failed</span>
                )}
                {testError && <span className="test-error-msg">{testError}</span>}
              </div>
              {testResponse != null && (
                <pre className="test-response-body">
                  {typeof testResponse === 'string' ? testResponse : JSON.stringify(testResponse, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </section>
    );
  };

  renderTreeNode = (node, depth = 0) => {
    const {expandedPaths} = this.state;
    const c = this.props.config;
    const expanded = !!expandedPaths[node.path];
    const selected = c.responseRootPath === node.path;

    return (
      <div key={node.path} className="response-tree-node">
        <button
          type="button"
          className={`response-tree-row${selected ? ' selected' : ''}${node.isLeaf ? ' leaf' : ''}`}
          style={{paddingLeft: `${12 + depth * 18}px`}}
          onClick={() => this.handleTreeNodeClick(node)}
        >
          {!node.isLeaf && (
            <span className="material-icons-outlined tree-chevron">
              {expanded ? 'expand_more' : 'chevron_right'}
            </span>
          )}
          {node.isLeaf && <span className="tree-spacer" />}
          <span className="tree-label">{node.label}</span>
          <span className="tree-value">{node.displayValue}</span>
          {node.isLeaf && selected && (
            <span className="material-icons-outlined tree-selected-icon">check_circle</span>
          )}
        </button>
        {!node.isLeaf && expanded && node.children.map((child) => this.renderTreeNode(child, depth + 1))}
      </div>
    );
  };

  renderResponsePathStep = () => {
    const c = this.props.config;
    const {testResponse} = this.state;
    const treeNodes = testResponse != null ? buildResponseTreeNodes(testResponse) : [];

    return (
      <section className="api-connector-card">
        {this.renderSectionHeader('account_tree', 'icon-table', 'Response path', 'Expand nodes and pick a leaf to set the response root path.')}
        <p className="api-connector-step-help">
          Click a branch to expand it. Click a leaf value to use that path as the response root.
        </p>
        {c.responseRootPath && (
          <div className="selected-path-banner">
            <span className="material-icons-outlined">route</span>
            <span>Selected path: <strong>{c.responseRootPath}</strong></span>
          </div>
        )}
        <div className="response-tree-panel">
          {treeNodes.length
            ? treeNodes.map((node) => this.renderTreeNode(node))
            : <p className="response-tree-empty">Run a successful test to browse the response tree.</p>}
        </div>
      </section>
    );
  };

  renderStepContent = () => {
    switch (this.state.step) {
      case 0: return this.renderConnectionStep();
      case 1: return this.renderSiteZoneStep();
      case 2: return this.renderTestStep();
      case 3: return this.renderResponsePathStep();
      default: return null;
    }
  };

  renderWizardNav = () => {
    const {step} = this.state;
    const isFirst = step === 0;
    const isLast = step === WIZARD_STEPS.length - 1;
    const canSave = step === 3 && this.state.testPassed;

    return (
      <div className="api-connector-wizard-nav">
        {!isFirst && (
          <button type="button" className="wizard-btn outlined" onClick={this.goBack}>
            Back
          </button>
        )}
        <div className="wizard-nav-spacer" />
        {step < 2 && (
          <button type="button" className="wizard-btn filled" onClick={this.goNext}>
            Next
          </button>
        )}
        {canSave && (
          <button type="button" className="wizard-btn filled" onClick={this.handleSave}>
            <span className="material-icons-outlined">save</span>
            Save connector
          </button>
        )}
      </div>
    );
  };

  render() {
    return (
      <div className="api-connector-config-form">
        {this.renderStepNav()}
        {this.renderStepContent()}
        {this.renderWizardNav()}
        {this.props.saveMessage && (
          <div className="save-success-msg">
            <span className="material-icons-outlined">check_circle</span>
            {this.props.saveMessage}
          </div>
        )}
      </div>
    );
  }
}

export default ApiConnectorConfigForm;
